import { useEffect, useState } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Users, TrendingUp, Activity, Target } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { fmtNumber } from "@/lib/format";
import KPI from "./KPI";

const COLORS = ["#e07a5f", "#0E1626", "#10b981", "#6366f1", "#f59e0b", "#ec4899"];

export default function GrowthTab() {
  const auth = useAdminAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    auth.apiGet(`/admin/growth?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se pudo cargar crecimiento"));
  }, [auth, days]);

  if (!data) return <p className="text-sm text-ink-mid p-8 text-center" data-testid="growth-loading">Cargando crecimiento…</p>;

  const t = data.totals;
  const roleData = Object.entries(data.role_breakdown).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div data-testid="tab-growth" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Analítica de crecimiento</p>
        <div className="flex gap-1 text-xs" data-testid="growth-range">
          {[7, 14, 30, 60, 90].map((d) => (
            <button
              key={d}
              data-testid={`growth-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full font-mono ${days === d ? "bg-ink text-cream" : "bg-cream-card hairline border"}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testid="kpi-total-users" icon={Users}    k={fmtNumber(t.total_users)} v="usuarios totales" highlight />
        <KPI testid="kpi-dau"          icon={Activity} k={fmtNumber(t.dau)}          v="DAU" sub="últimos 24h" />
        <KPI testid="kpi-mau"          icon={Activity} k={fmtNumber(t.mau)}          v="MAU" sub="últimos 30d" />
        <KPI testid="kpi-stickiness"   icon={Target}   k={`${t.stickiness_pct}%`}    v="stickiness" sub="DAU / MAU" />
        <KPI testid="kpi-pro-conv"     icon={TrendingUp} k={`${t.pro_conversion_pct}%`} v="conversión PRO" sub={`${fmtNumber(t.pro_users)} usuarios`} />
      </section>

      <section className="border hairline bg-cream-card rounded-2xl p-5" data-testid="growth-timeline">
        <h3 className="font-serif text-xl tracking-tight mb-3">Signups · últimos {days} días</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <LineChart data={data.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0E162610" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="total" stroke="#e07a5f" strokeWidth={2} name="Total signups" />
              <Line type="monotone" dataKey="beta_signups" stroke="#0E1626" strokeWidth={1.5} name="Beta" />
              <Line type="monotone" dataKey="demo_signups" stroke="#6366f1" strokeWidth={1.5} name="Demo" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <section className="border hairline bg-cream-card rounded-2xl p-5" data-testid="growth-roles">
          <h3 className="font-serif text-xl tracking-tight mb-3">Distribución por rol</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={roleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                  {roleData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="border hairline bg-cream-card rounded-2xl p-5" data-testid="growth-geo">
          <h3 className="font-serif text-xl tracking-tight mb-3">Top países</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.geo_breakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0E162610" />
                <XAxis dataKey="country" tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#e07a5f" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>
    </div>
  );
}
